import { bundlePrefix, bundlesPath } from 'config/shared';
import { NonCriticalError } from 'app/constants/NonCriticalError';
import {
  ResponseError,
  TimeoutError,
  NetworkError,
  AbortedError,
} from 'app/constants/ResponseErrors';
import { LoginFailure } from 'app/components/auth/LoginContext';
import OauthError from 'app/services/oauth/OauthError';
import * as Honeybadger from 'app/services/honeybadger';
import FluentdLogger from 'app/services/fluentdLogger';
import RealtimeError from 'app/services/rtc/RealtimeError';
import { SocketAbortedError, SocketSendError, SocketTimeoutError } from 'app/constants/SocketErrors';

const BUNDLE_PATH_REGEXP = new RegExp(
  // Includes either the vendor, chunk or main prefix.
  `${bundlesPath}/(${Object.values(bundlePrefix).join('|')})` +


  // May or may not contain hash data.
  '(-[A-Za-z0-9]+)?' +

  // Ends with a js extension.
  '\\.js',
);

/**
 * A critical error is an error that developers definitely should be notified of.
 *
 * @param {Error} error
 * @returns {Boolean}
 */
export const isCriticalError = (error) => {
  if (error instanceof ResponseError) {
    const isNonCriticalStatus = ['400', '401', '403', '404', '503'].includes(String(error.status));

    return (
      !isNonCriticalStatus &&
      !(error instanceof AbortedError) &&
      !(error instanceof NetworkError) &&
      !(error instanceof TimeoutError)
    );
  } else if (error instanceof SocketSendError) {
    return (
      !(error instanceof SocketAbortedError) &&
      !(error instanceof SocketTimeoutError)
    );
  } else if (error instanceof NonCriticalError) {
    return false;
  } else if (error instanceof OauthError) {
    return error.oauthError.error !== OauthError.Codes.INVALID_GRANT;
  } else if (error instanceof LoginFailure) {
    return isCriticalError(error.cause);
  } else if (error && error.type && error.type === 'TransportError') {
    // socket.io error
    return false;
  } else if (error && error.code && (
    error.code === RealtimeError.Codes.NoSuchPeer ||
    error.code === RealtimeError.Codes.PeerAckTimeout ||
    error.code === 'Error.AckTimeout' // TODO: Legacy, remove once PR#356 on Realtime is out
  )) {
    return false;
  } else if (error && error.stack) {
    return BUNDLE_PATH_REGEXP.test(String(error.stack));
  } else {
    // This probably means a string was thrown, the source is difficult to tell.
    return true;
  }
};

/**
 * Takes information dispatched from the error reporter and dispatches it to the appropriate
 * diagnostic service.
 *
 * @param {Error} err
 * @param {Object} metaData
 */
export const dispatchToDiagnosticService = (err, metaData) => {
  if (isCriticalError(err)) {
    Honeybadger.notify(err, metaData);
  } else {
    const log = {
      name: metaData.name || err.name,
      message: err.message,
    };

    if (err instanceof ResponseError) {
      log.request_status = err.status;
      log.request_url = err.url;
      log.request_method = err.method;
    }

    FluentdLogger.info(log);
  }
};
